"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, Sparkles } from "lucide-react";

interface StickyCtaBarProps {
  text: string;
  ctaText: string;
  ctaHref: string;
  /** Po jakiej części wysokości okna pasek ma się pojawić (0-1). Domyślnie 0.7. */
  threshold?: number;
}

// Elegancki pasek CTA przyklejony do dołu, pojawia się po przewinięciu poza
// pierwszą sekcję. Reużywalny na landingach (/kursy-maturalne, /matura-2027).
// Wzorzec stabilnego drzewa: AnimatePresence renderowany zawsze, dziecko warunkowo.
export default function StickyCtaBar({ text, ctaText, ctaHref, threshold = 0.7 }: StickyCtaBarProps) {
  const [scrolledPast, setScrolledPast] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolledPast(window.scrollY > window.innerHeight * threshold);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  const show = scrolledPast && !dismissed;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="sticky-cta-bar"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 32 }}
          className="fixed inset-x-0 bottom-[4.5rem] z-40 px-3 sm:bottom-4 sm:px-6"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-2 rounded-2xl border border-slate-200 bg-white/95 px-3 py-3 shadow-2xl shadow-slate-900/10 backdrop-blur sm:flex-row sm:items-center sm:gap-5 sm:px-6 sm:py-4">
            <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg sm:flex">
              <Sparkles className="h-5 w-5" />
            </span>
            <p className="min-w-0 flex-1 text-center text-[13px] font-semibold leading-snug text-slate-800 sm:text-left sm:text-base">
              {text}
            </p>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link
                href={ctaHref}
                className="group inline-flex w-full shrink-0 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-lg shadow-amber-500/25 transition-transform hover:scale-[1.03] sm:w-auto sm:px-6 sm:py-3"
              >
                <span className="whitespace-nowrap">{ctaText}</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <button
                type="button"
                aria-label="Zamknij pasek"
                onClick={() => setDismissed(true)}
                className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
